import { Container, Graphics, Sprite, Texture, Application } from 'pixi.js';
import { BALL_RADIUS } from './constants';

interface GoldRing {
    x: number;
    y: number;
    sprite: Sprite;
    collected: boolean;
    rotation: number;
}

export default class GoldRingManager {
    private app: Application;
    private gameWorld: Container;
    private rings: GoldRing[] = [];
    private ringTexture: Texture | null = null;

    constructor(app: Application, gameWorld: Container) {
        this.app = app;
        this.gameWorld = gameWorld;
        this.createRingTexture();
    }

    private createRingTexture(): void {
        const graphics = new Graphics();
        const outerRadius = 25;
        const innerRadius = 15;
        
        // Draw outer ring
        graphics.circle(0, 0, outerRadius);
        graphics.fill({ color: 0xFFD700, alpha: 0.9 });
        
        // Draw inner hole
        graphics.circle(0, 0, innerRadius);
        graphics.clear();
        
        // Draw ring outline
        graphics.circle(0, 0, outerRadius);
        graphics.stroke({ width: 3, color: 0xFFA500 });
        graphics.circle(0, 0, innerRadius);
        graphics.stroke({ width: 2, color: 0xFFD700 });
        
        this.ringTexture = this.app.renderer.generateTexture(graphics);
    }

    public createRing(x: number, y: number, index: number): void {
        if (!this.ringTexture) {
            this.createRingTexture();
        }

        const sprite = new Sprite(this.ringTexture);
        sprite.anchor.set(0.5);
        sprite.x = x;
        sprite.y = y;
        sprite.scale.set(1);
        
        this.gameWorld.addChild(sprite);

        this.rings.push({
            x,
            y,
            sprite,
            collected: false,
            rotation: 0
        });
    }

    public collectRing(index: number): GoldRing | null {
        const ring = this.rings[index];
        if (ring && !ring.collected) {
            ring.collected = true;
            return ring;
        }
        return null;
    }

    public update(delta: number, targetPosition?: {x: number, y: number}, magnetRadius: number = 0): void {
        this.rings.forEach((ring, index) => {
            if (!ring.collected) {
                // Magnet attraction
                if (targetPosition && magnetRadius > 0) {
                    const dx = targetPosition.x - ring.x;
                    const dy = targetPosition.y - ring.y;
                    const distSq = dx * dx + dy * dy;
                    
                    if (distSq < magnetRadius * magnetRadius) {
                        const dist = Math.sqrt(distSq);
                        const speed = 15 * (1 - dist / magnetRadius) + 5;
                        ring.x += (dx / dist) * speed * delta;
                        ring.y += (dy / dist) * speed * delta;
                        ring.sprite.x = ring.x;
                        ring.sprite.y = ring.y;
                    }
                }

                // Rotate the ring
                ring.rotation += delta * 0.05;
                ring.sprite.rotation = ring.rotation;
                
                // Pulse effect
                const pulse = 1 + Math.sin(Date.now() * 0.003 + index) * 0.15;
                ring.sprite.scale.set(pulse);
                
                // Glow effect
                const alpha = 0.7 + Math.sin(Date.now() * 0.005 + index) * 0.3;
                ring.sprite.alpha = alpha;
            } else {
                // Animate collection
                ring.sprite.scale.x *= 0.9;
                ring.sprite.scale.y *= 0.9;
                ring.sprite.alpha *= 0.8;
                
                if (ring.sprite.alpha < 0.1) {
                    this.gameWorld.removeChild(ring.sprite);
                    this.rings.splice(index, 1);
                }
            }
        });
    }

    public removeRing(index: number): void {
        const ring = this.rings[index];
        if (ring && ring.sprite.parent) {
            this.gameWorld.removeChild(ring.sprite);
            this.rings.splice(index, 1);
        }
    }

    public clearAll(): void {
        this.rings.forEach(ring => {
            if (ring.sprite.parent) {
                this.gameWorld.removeChild(ring.sprite);
            }
        });
        this.rings = [];
    }

    public getRings(): GoldRing[] {
        return this.rings;
    }
}

